import { useForm } from 'react-hook-form';
import { TuneCoreVersionDetailsFormData } from 'app/components/Releases/TuneCoreVersionDetailsForm/types';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  getDefaultValues,
  getUpdateVersionValidationSchema,
} from 'app/components/Releases/TuneCoreVersionDetailsForm/utils';
import { useEffect, useMemo } from 'react';
import {
  ReleaseVersion,
  UpdateReleaseVersionValidations,
} from 'types/releases';
import { DDL } from 'types/general.types';
import { getValidDirtyValues } from 'app/utils/utils';

interface UseTCVersionFormProps {
  releaseVersion: ReleaseVersion;
  genresDDL: DDL;
  versionValidations: UpdateReleaseVersionValidations;
  updateVersion: (
    data: Partial<TuneCoreVersionDetailsFormData>
  ) => Promise<void>;
  languageCodes: DDL;
}

const versionFieldKeys = ['versionTitle', 'explicit', 'language'];

export const useTCVersionForm = ({
  releaseVersion,
  genresDDL,
  versionValidations,
  updateVersion,
  languageCodes,
}: UseTCVersionFormProps) => {
  const defaultValues = useMemo(
    () => getDefaultValues(releaseVersion, genresDDL, languageCodes),
    [releaseVersion, genresDDL, languageCodes]
  );

  const {
    handleSubmit,
    control,
    formState,
    reset,
    setValue,
    watch,
    register,
    getValues,
  } = useForm<TuneCoreVersionDetailsFormData>({
    defaultValues,
    mode: 'all',
    shouldFocusError: false,
    resolver: yupResolver(getUpdateVersionValidationSchema(versionValidations)),
  });

  const { dirtyFields, errors } = formState;
  const watchGenre = watch('genre');

  const submitHandler = async () => {
    const data = getValues();
    const validDirtyValues = getValidDirtyValues<TuneCoreVersionDetailsFormData>(
      errors,
      dirtyFields,
      data
    );
    const dirtyKeys = Object.keys(validDirtyValues);
    if (dirtyKeys.length) {
      const shouldUpdateVersion = dirtyKeys.some(
        (key) => versionFieldKeys.indexOf(key) >= 0
      );
      if (shouldUpdateVersion) {
        reset(data, { keepErrors: true });
        await updateVersion(validDirtyValues);
      }
    }
  };

  const onSubmit = handleSubmit(submitHandler, submitHandler);

  useEffect(() => {
    reset(defaultValues);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [releaseVersion.song.snapshotUpdated.timestamp]);

  return {
    onSubmit,
    control,
    setValue,
    watchGenre,
    register,
    errors,
  };
};
